import { Currency } from '@round/api';
import { MoneyInput, MoneyValue, Table, Tooltip, lucideIcons } from '@round/creatorbase-ui';
import { useCreatorbaseCurrencies } from '@round/creatorbase-helpers/api-hooks';
import { ResultCreator } from '../../../ResultCreator/ResultCreator';
import { TotalCostCell } from './components/TotalCostCell/TotalCostCell';
import { OutreachResultRow } from '../../../../helpers';
import styles from './ResultCostsTable.module.css';

const { Info, AlertTriangle } = lucideIcons;

const CREATOR_HEADER = 'Creator';
const COST_HEADER = 'Cost';
const TOTAL_COST_HEADER = 'Total cost';
const TOTAL_COST_HINT = 'Includes estimated fees';
const COST_PLACEHOLDER = 'Enter cost';
const CURRENCY_MISMATCH_HINT = "Rate is in a different currency than this campaign, so it hasn't been filled in";

type Props = {
    results: OutreachResultRow[];
    campaignId: number;
    campaignCurrency: Currency | undefined;
    getCost: (resultId: number) => MoneyValue;
    onCostChange: (resultId: number, cost: MoneyValue) => void;
    onCostBlur: (resultId: number) => void;
    missingCostIds: Set<number>;
    /** A result whose rate is priced in a currency the (fee-less) campaign can't take, so it wasn't prefilled. */
    mismatchedCurrencyIds: Set<number>;
    /** Off when the campaign costs its posts itself. */
    isCostEditable: boolean;
    /** Only a fee-paying client costs a post in a currency other than the campaign's. */
    areFeesEnabled: boolean;
    isDisabled: boolean;
    getAvatarUrl: (userId: number) => string | undefined;
    areAvatarsLoading: boolean;
};

export const ResultCostsTable = ({
    results,
    campaignId,
    campaignCurrency,
    getCost,
    onCostChange,
    onCostBlur,
    missingCostIds,
    mismatchedCurrencyIds,
    isCostEditable,
    areFeesEnabled,
    isDisabled,
    getAvatarUrl,
    areAvatarsLoading,
}: Props) => {
    const { currencyOptions } = useCreatorbaseCurrencies();
    const isTotalCostShown = isCostEditable && areFeesEnabled;

    return (
        <Table.Container className={styles.container}>
            <Table>
                <Table.Header className={styles.header}>
                    <Table.Row>
                        <Table.Head>{CREATOR_HEADER}</Table.Head>
                        {isCostEditable && <Table.Head className={styles.costHead}>{COST_HEADER}</Table.Head>}
                        {isTotalCostShown && (
                            <Table.Head className={styles.totalCostHead}>
                                <span className={styles.totalCostLabel}>
                                    {TOTAL_COST_HEADER}
                                    <Tooltip>
                                        <Tooltip.Trigger aria-label={TOTAL_COST_HINT}>
                                            <Info className={styles.infoIcon} />
                                        </Tooltip.Trigger>
                                        <Tooltip.Content>{TOTAL_COST_HINT}</Tooltip.Content>
                                    </Tooltip>
                                </span>
                            </Table.Head>
                        )}
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {results.map((result) => (
                        <Table.Row key={result.id}>
                            <Table.Cell className={styles.creatorCell}>
                                <ResultCreator
                                    username={result.username}
                                    avatarUrl={getAvatarUrl(result.user_id)}
                                    isAvatarLoading={areAvatarsLoading}
                                />
                            </Table.Cell>

                            {isCostEditable && (
                                <Table.Cell className={styles.costCell}>
                                    <span className={styles.costInputRow}>
                                        <MoneyInput
                                            aria-label={`Cost for @${result.username}`}
                                            placeholder={COST_PLACEHOLDER}
                                            currencies={currencyOptions}
                                            isCurrencyDisabled={!areFeesEnabled}
                                            disabled={isDisabled}
                                            aria-invalid={missingCostIds.has(result.id)}
                                            value={getCost(result.id)}
                                            onChange={(cost) => onCostChange(result.id, cost)}
                                            onBlur={() => onCostBlur(result.id)}
                                        />

                                        {mismatchedCurrencyIds.has(result.id) && (
                                            <Tooltip>
                                                <Tooltip.Trigger aria-label={CURRENCY_MISMATCH_HINT}>
                                                    <AlertTriangle className={styles.mismatchIcon} />
                                                </Tooltip.Trigger>
                                                <Tooltip.Content>{CURRENCY_MISMATCH_HINT}</Tooltip.Content>
                                            </Tooltip>
                                        )}
                                    </span>
                                </Table.Cell>
                            )}

                            {isTotalCostShown && (
                                <Table.Cell className={styles.totalCostCell}>
                                    <TotalCostCell
                                        campaignId={campaignId}
                                        campaignCurrency={campaignCurrency}
                                        cost={getCost(result.id)}
                                    />
                                </Table.Cell>
                            )}
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </Table.Container>
    );
};
